import { useCallback, useRef, useState } from 'react'

/**
 * Navegação com cursor keyset.
 *
 * O cursor da API é **opaco e só aponta para frente** (`comum/paginacao.ts`): não
 * existe "cursor anterior". Para o botão `Anterior` funcionar, o cliente guarda a
 * pilha dos cursores que já usou — voltar é descartar o topo.
 *
 * A pilha vive em memória, e não na URL, de propósito: uma URL com dez cursores
 * base64 encadeados é impossível de compartilhar, e o link que o corretor manda
 * para o gestor deve levar aos **filtros**, não à décima página deles.
 *
 * `reiniciar` é chamado a cada mudança de filtro: o cursor é keyset sobre a
 * consulta anterior e, mantido, devolveria uma fatia sem relação com o novo
 * recorte.
 *
 * **O prefixo `use` não é anglicismo por descuido** — é o mesmo padrão dos outros
 * cinco ganchos do diretório (`useDebounce`, `useTema`, `useFormulario`,
 * `useFiltrosUrl`, `useCatalogoAtivo`), e é obrigatório: `react-hooks` identifica
 * gancho customizado **pelo nome**. Com `usarPaginacaoCursor`, o plugin não
 * reconhecia esta função como gancho, acusava as oito chamadas internas como
 * violação de `rules-of-hooks` e — o que custava mais — deixava de conferir as
 * dependências nos três componentes que a consomem. O nome do arquivo continua em
 * português, como nos irmãos.
 */
export function usePaginacaoCursor(limiteInicial = 50) {
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [limite, setLimite] = useState(limiteInicial)
  const pilha = useRef<Array<string | undefined>>([])
  const [pagina, setPagina] = useState(1)

  const avancar = useCallback(
    (proximoCursor: string | null) => {
      if (!proximoCursor) return
      pilha.current.push(cursor)
      setCursor(proximoCursor)
      setPagina((valor) => valor + 1)
    },
    [cursor],
  )

  const voltar = useCallback(() => {
    if (pilha.current.length === 0) return
    setCursor(pilha.current.pop())
    setPagina((valor) => Math.max(1, valor - 1))
  }, [])

  const reiniciar = useCallback(() => {
    pilha.current = []
    setCursor(undefined)
    setPagina(1)
  }, [])

  const alterarLimite = useCallback(
    (novo: number) => {
      setLimite(novo)
      pilha.current = []
      setCursor(undefined)
      setPagina(1)
    },
    [],
  )

  return {
    cursor,
    limite,
    pagina,
    temAnterior: pilha.current.length > 0,
    /** Índice do primeiro item da página, para o "Mostrando X a Y". */
    indiceInicial: (pagina - 1) * limite + 1,
    avancar,
    voltar,
    reiniciar,
    alterarLimite,
  }
}
